/**
 * Modal-ready oasis PDF viewer using {@link PdfRenderer} and
 * {@link OasisPdfToolbar}.
 *
 * Rotation is owned here because oasis has no rotate API: the angle is kept in
 * local state and applied to `.pdf-renderer__page` via a scoped style.
 * `worker` defaults to off so wasm load failures surface instead of hanging
 * (same rationale as the Candid auth-letter panel); full-page hosts rendering
 * large packets can opt back in.
 *
 * @module OasisPdfViewerPanel
 */
import { PdfRenderer } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useState } from "react";
import { DocumentPageMapping } from "../../data-model/medicalPaOrdersModel";
import { logError } from "../../utils/customLogger";
import { DocumentMappingList } from "../documentMappingList/DocumentMappingList";
import { OasisPdfToolbar, PdfToolbarFeedback } from "./OasisPdfToolbar";

/** Self-hosted PDFium binary served from `public/`. */
const PDFIUM_WASM_URL = "/pdfium.wasm";

/** Selector used to size the document index against this panel. */
const PANEL_SELECTOR = ".oasis-pdf-viewer-panel";

/** Props for {@link OasisPdfViewerPanel}. */
export interface OasisPdfViewerPanelProps {
  /** Signed URL of the PDF to render. */
  pdfUrl: string;
  /** Per-source-document page ranges; omitted when the index should hide. */
  documentPageMapping?: DocumentPageMapping[];
  /**
   * Renders pages off the main thread. Off by default so wasm failures
   * surface; enable for full-page viewers of large documents.
   */
  worker?: boolean;
  /**
   * When true, shows thumbs-up / thumbs-down on the toolbar. Used for the
   * clinical document viewer on the medical PA form (and matching viewers).
   */
  showFeedback?: boolean;
  /** Controlled thumbs selection when `showFeedback` is true. */
  feedback?: PdfToolbarFeedback;
  /** Invoked when the reviewer clicks a feedback thumb. */
  onFeedback?: (feedback: PdfToolbarFeedback) => void;
}

/**
 * Renders a full-height oasis PDF viewer with search, zoom, and rotate.
 *
 * @input props - PDF URL, optional document page mapping, worker toggle
 * @input props - PDF URL, optional document page mapping, and optional feedback
 * @return The oasis PDF viewer panel
 */
const OasisPdfViewerPanel: React.FC<OasisPdfViewerPanelProps> = ({
  pdfUrl,
  documentPageMapping,
  worker = false,
  showFeedback = false,
  feedback,
  onFeedback,
}) => {
  const [rotation, setRotation] = useState(0);
  const mappings = documentPageMapping ?? [];

  /** Advances page rotation by 90° (0 → 90 → 180 → 270 → 0). */
  const rotatePage = () => setRotation((prev) => (prev + 90) % 360);

  return (
    <div className="oasis-pdf-viewer-panel flex h-full min-h-0 w-full flex-col overflow-hidden">
      {rotation !== 0 && (
        <style>
          {`.oasis-pdf-viewer-panel .pdf-renderer__page {` +
            ` transform: rotate(${rotation}deg); transform-origin: center center; }`}
        </style>
      )}
      <PdfRenderer
        src={pdfUrl}
        wasmUrl={PDFIUM_WASM_URL}
        worker={worker}
        className="h-full w-full !rounded-none !border-0"
        height="100%"
        width="100%"
        features={{
          zoom: true,
          search: true,
          textSelection: true,
          thumbnails: false,
          pageNavigation: true,
        }}
        onError={(error) =>
          logError(
            error ?? new Error("Unknown PDF renderer error"),
            "Oasis PDF viewer failed to render",
          )
        }
        renderError={(error) => (
          <div className="flex h-full w-full items-center justify-center p-4 text-sm text-red-600">
            {error?.message || "Unable to load this document."}
          </div>
        )}
        toolbar={(ctx) => (
          <OasisPdfToolbar
            ctx={ctx}
            rotation={rotation}
            onRotate={rotatePage}
            showFeedback={showFeedback}
            feedback={feedback}
            onFeedback={onFeedback}
            leadingContent={
              mappings.length > 0 ? (
                <DocumentMappingList
                  mappings={mappings}
                  onSelectPage={(page) => ctx.goToPage(page)}
                  containerSelector={PANEL_SELECTOR}
                />
              ) : undefined
            }
          />
        )}
      />
    </div>
  );
};

export default OasisPdfViewerPanel;
export { OasisPdfViewerPanel };
